import cn from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Sortable from 'sortablejs';

import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Icon } from '@/shared/ui';

import { albumModel } from '@/entities/album';
import { ModalID, modalModel, RemoveModal } from '@/entities/modal';
import {
  organizationModel,
  useStorageBlockedTooltip,
} from '@/entities/organization';

import { TransformedPhoto } from '@/features/transformedPhoto';

import {
  AlbumPopover,
  useDentalPhotoAlbumActions,
} from '@/widgets/DentalPhotoReportCard';

import { PHOTOS_ANIMATION_DURATION } from '../../config';

import styles from './Album.module.scss';

type AlbumProps = {
  album: DentalPhotoAlbum;
  canEditAlbums: boolean;
  canUpload: boolean;
  isStorageBlocked: boolean;
  patientID: string;
  className?: string;
  isProcessingUpload?: boolean;
  testID?: string;
};

export const Album: FC<AlbumProps> = (props) => {
  const {
    album,
    canEditAlbums,
    canUpload,
    className,
    isProcessingUpload,
    isStorageBlocked,
    patientID,
    testID,
  } = props;
  const { formatDate, formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const dispatch = useAppDispatch();

  const { wrapWithStorageTooltip } = useStorageBlockedTooltip({
    isStorageBlocked,
  });
  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );
  const initialPhotoIDs = album.StudyAssetIDs;

  const [photoIDs, setPhotoIDs] = useState(initialPhotoIDs);
  const photosRef = useRef<HTMLDivElement>(null);
  const sortableRef = useRef<Sortable | null>(null);

  const albumID = album.ID;
  const albumName = album.Name;
  const albumTime = album.Time?.toDate();

  const {
    isDownloading,
    isDeleting,
    isRemoveModalOpen,
    openRemoveModal,
    closeRemoveModal,
    handleEditAlbum,
    handleDownloadAlbum,
    handleConfirmDeleteAlbum,
    handleUploadToAlbum,
    handleMobileUploadToAlbum,
  } = useDentalPhotoAlbumActions({ album, patientID });

  useEffect(() => {
    setPhotoIDs(initialPhotoIDs);
  }, [initialPhotoIDs]);

  useEffect(() => {
    if (photosRef.current && canEditAlbums && photoIDs.length > 0) {
      const sortableOnStart = () => {
        const ghosts = photosRef.current?.querySelectorAll('.sortable-ghost');
        ghosts?.forEach((ghost) => ghost.classList.remove('sortable-ghost'));
      };

      const sortableOnEnd = async (event: Sortable.SortableEvent) => {
        const ghosts = photosRef.current?.querySelectorAll('.sortable-ghost');
        ghosts?.forEach((ghost) => ghost.classList.remove('sortable-ghost'));

        const chosen = photosRef.current?.querySelectorAll('.sortable-chosen');
        chosen?.forEach((element) =>
          element.classList.remove('sortable-chosen'),
        );
        if (event.oldIndex === undefined || event.newIndex === undefined) {
          return;
        }

        const newPhotoIDs = [...photoIDs];
        const [movedPhotoID] = newPhotoIDs.splice(event.oldIndex, 1);
        newPhotoIDs.splice(event.newIndex, 0, movedPhotoID);

        trackEvent({
          eventName: 'DentalPhotosChangePhotoPlaceInAlbumDragDrop',
          properties: {
            photo_id: movedPhotoID,
            album_id: albumID,
          },
        });

        setPhotoIDs(newPhotoIDs);

        if (albumID) {
          await dispatch(
            albumModel.thunks.reorderAlbumAssets({
              AlbumID: albumID,
              OrderedAssetIDs: newPhotoIDs,
            }),
          );
        }
      };

      sortableRef.current = Sortable.create(photosRef.current, {
        animation: PHOTOS_ANIMATION_DURATION,
        ghostClass: 'sortable-ghost',
        dragClass: 'sortable-drag',
        chosenClass: 'sortable-chosen',
        draggable: `.${styles.photoItem}`,
        filter: `.${styles.uploadButton}`,
        preventOnFilter: false,
        delay: 100,
        delayOnTouchOnly: true,
        touchStartThreshold: 5,
        forceFallback: false,
        fallbackTolerance: 5,
        bubbleScroll: true,
        removeCloneOnHide: true,
        scrollSensitivity: 30,
        scrollSpeed: 10,
        emptyInsertThreshold: 5,
        onStart: sortableOnStart,
        onEnd: sortableOnEnd,
      });
    }

    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
    };
  }, [canEditAlbums, dispatch, photoIDs, albumID]);

  return (
    <div data-testid={testID} className={cn(styles.container, className)}>
      <header className={cn(styles.albumHeader, isMobile ? 'p1m' : 'p1')}>
        <div className={styles.albumInfo}>
          {albumName && `${albumName},`}{' '}
          {albumTime && formatDate(albumTime, { dateStyle: 'long' })}
        </div>

        <AlbumPopover
          canEditAlbums={canEditAlbums}
          className={styles.albumPopover}
          isDownloading={isDownloading}
          isDeleting={isDeleting}
          onEditAlbum={handleEditAlbum}
          onDownloadAlbum={handleDownloadAlbum}
          onDeleteAlbum={openRemoveModal}
        />
      </header>

      <div className={styles.photos}>
        <div ref={photosRef} className={styles.sortableContainer}>
          {photoIDs.map((photoID, index) => (
            <div
              key={photoID}
              className={styles.photoItem}
              data-component="photo-preview"
              data-id={photoID}
              onClick={() => {
                trackEvent({
                  eventName: 'DentalPhotosPhotoClicked',
                  properties: {
                    photo_id: photoID,
                    album_id: albumID,
                  },
                });

                dispatch(
                  modalModel.actions.openModal({
                    modalID: ModalID.DentalPhotoSlider,
                    data: {
                      albumID: albumID,
                      activePhotoID: photoID,
                      canEditAlbums: canEditAlbums ?? false,
                    },
                  }),
                );
              }}
            >
              <TransformedPhoto
                photoID={photoID}
                size="preview"
                forceSquare
                alt={`${formatMessage({
                  id: 'imgAltText.photo',
                  defaultMessage: 'Photo',
                })} ${index + 1}`}
              />
            </div>
          ))}

          {isProcessingUpload && <div className={styles.skeletonPhoto} />}

          {canUpload &&
            wrapWithStorageTooltip(
              isMobile ? (
                <div
                  className={cn(
                    styles.uploadButton,
                    (isStorageBlocked || isForbidUploadsFromWeb) &&
                      styles.disabled,
                  )}
                  onClick={
                    isStorageBlocked || isForbidUploadsFromWeb
                      ? undefined
                      : handleMobileUploadToAlbum
                  }
                >
                  <div className={styles.circle}>
                    <Icon
                      name="boldPlus"
                      size={24}
                      className={styles.uploadIcon}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className={styles.uploadButton}
                  onClick={handleUploadToAlbum}
                >
                  <div className={styles.circle}>
                    <Icon
                      name="boldPlus"
                      size={24}
                      className={styles.uploadIcon}
                    />
                  </div>
                </div>
              ),
            )}
        </div>
      </div>

      <RemoveModal
        isOpen={isRemoveModalOpen}
        message={formatMessage({
          id: 'dentalPhoto.removePhotoModal.message',
          defaultMessage: 'This action is irreversible',
        })}
        title={formatMessage({
          id: 'dentalPhoto.removeAlbumModal.title',
          defaultMessage: 'Remove this album',
        })}
        onCancel={closeRemoveModal}
        onRemoveCallback={handleConfirmDeleteAlbum}
      />
    </div>
  );
};
